import React from 'react';
import { getAvatarKey, type StaffAvatarManifest } from '../../utils/avatarUrl';
import {
  useStaffAvatarBaseUrl,
  useStaffAvatarManifest,
  useStaffAvatarsLive,
} from './StaffAvatarManifest';
import { pickAvatarSrc, UserAvatar, type UserAvatarSize } from './UserAvatar';

interface StaffPhotoAvatarProps {
  readonly email?: string | null;
  readonly name?: string | null;
  readonly size?: UserAvatarSize;
  readonly label?: string;
  /** The photo URL this surface showed before the staff list. */
  readonly legacySrc?: string | null;
  /**
   * The author may not be staff (a vendor writing a claim note). Only someone the staff list
   * names switches to UserAvatar; everyone else keeps today's avatar.
   */
  readonly mayBeExternal?: boolean;
  /** Today's avatar, rendered unchanged outside the Cowbell persona. */
  readonly children: React.ReactNode;
}

export type StaffPhotoAvatarMode = 'legacy' | 'staff';

/**
 * Which avatar a surface that already showed photos renders. Outside the Cowbell persona,
 * always today's. Inside it, UserAvatar, which keeps the old photo until the list is live;
 * for an author who may be external, only once the list is live and names them.
 */
export function staffPhotoAvatarMode({
  manifest,
  isLive,
  email,
  mayBeExternal = false,
}: Readonly<{
  manifest: StaffAvatarManifest | undefined;
  isLive: boolean;
  email?: string | null;
  mayBeExternal?: boolean;
}>): StaffPhotoAvatarMode {
  if (manifest === undefined) {
    return 'legacy';
  }
  if (!mayBeExternal) {
    return 'staff';
  }
  return isLive && isListedStaff(manifest, email) ? 'staff' : 'legacy';
}

/** Whether the staff list names this email (only staff with a photo are listed). */
export function isListedStaff(
  manifest: StaffAvatarManifest | undefined,
  email?: string | null
): boolean {
  const key = getAvatarKey(email);
  return Boolean(
    manifest && key && Object.prototype.hasOwnProperty.call(manifest, key)
  );
}

/**
 * Moves a surface that already showed staff photos onto UserAvatar in the Cowbell persona,
 * passing its old photo as legacySrc, and renders `children` (today's avatar) everywhere else.
 */
export function StaffPhotoAvatar({
  email,
  name,
  size = 'sm',
  label,
  legacySrc,
  mayBeExternal,
  children,
}: StaffPhotoAvatarProps) {
  const manifest = useStaffAvatarManifest();
  const isLive = useStaffAvatarsLive();
  const mode = staffPhotoAvatarMode({ manifest, isLive, email, mayBeExternal });
  if (mode === 'legacy') {
    return <>{children}</>;
  }
  return (
    <UserAvatar
      email={email}
      name={name}
      size={size}
      label={label}
      legacySrc={legacySrc}
    />
  );
}

/**
 * The staff photo URL to try for someone who may or may not be staff (a cmail sender): the old
 * URL until the staff list is live, then the list's thumbnail, or null when the list does not
 * name them, so nothing is probed. Outside the Cowbell persona, always the old URL.
 */
export function useStaffPhotoCandidate(
  email: string | null | undefined,
  legacySrc: string | null | undefined
): string | null {
  const manifest = useStaffAvatarManifest();
  const isLive = useStaffAvatarsLive();
  const baseUrl = useStaffAvatarBaseUrl();
  return pickAvatarSrc({ isLive, manifest, email, legacySrc, baseUrl });
}
